/**
 * Темп: какие пороги брать, чтобы кадры шли ровно.
 *
 * Наивные равные пороги дают рывок. Причина не в реализации, а в геометрии:
 * у растущей точки площадь идёт как квадрат радиуса, у волнового фронта —
 * иначе, и одинаковый шаг по значению поля означает очень разный шаг
 * по закрытой площади. Замер на полутоновой точке, доля покрытия по кадрам:
 *
 *   наивно        0 · 0.9 · 3.8 · 8.6 · 14.9 · 23.3 · 33.7 · 45.6 · 59.4 · …
 *   по квантилям  0.1 · 7.8 · 15.8 · 23.3 · 30.9 · 39.1 · 46.7 · 54.1 · …
 *
 * Первая последовательность треть перехода почти стоит на месте, а потом
 * наверстывает. Это читается не как замысел, а как подтормаживание.
 *
 * Лечится гистограммой: считаем распределение значений поля и берём пороги
 * на нужных ДОЛЯХ ПЛОЩАДИ. Стоит доли миллисекунды и работает для любого
 * поля — в том числе для тех, которых ещё нет.
 */

/** Как распределить площадь по кадрам. Возвращает долю 0…1 для доли времени. */
export type Tempo = (progress: number) => number

export const TEMPO: Record<string, Tempo> = {
  /** Равные приросты площади. База для сравнения механизмов. */
  linear: (p) => p,
  /** Быстрый старт, длинный хвост: чернила расходятся и добирают углы. */
  ink: (p) => 1 - Math.pow(1 - p, 1.7),
  /** Разгон: сначала едва заметно, потом захватывает. Для прогорания. */
  catch: (p) => Math.pow(p, 1.45),
  /**
   * Замедление к концу.
   *
   * Для разворачивающегося листа это не вкус, а геометрия: проекция панели
   * равна h·cos θ, то есть у настоящей развёртки конец медленнее начала.
   */
  unfold: (p) => 1 - Math.cos((p * Math.PI) / 2),
}

const BINS = 256

/**
 * Пороги по квантилям поля.
 *
 * Последний порог всегда выше максимума поля: маска обязана закрыться
 * полностью. Иначе на элементе после перехода навсегда остаются дыры —
 * ошибка, которую замечают не сразу и потом долго ищут.
 */
export function thresholds(field: Float32Array, frames: number, tempo: Tempo): Float32Array {
  const hist = new Uint32Array(BINS)
  for (let i = 0; i < field.length; i++) {
    const v = field[i]!
    const bin = v <= 0 ? 0 : v >= 1 ? BINS - 1 : (v * BINS) | 0
    hist[bin]!++
  }

  // Накопленная сумма — это и есть доля площади, закрытая при данном пороге.
  const cdf = new Float32Array(BINS)
  let acc = 0
  for (let b = 0; b < BINS; b++) {
    acc += hist[b]!
    cdf[b] = acc / field.length
  }

  const out = new Float32Array(frames)
  let bin = 0
  for (let k = 0; k < frames; k++) {
    if (k === frames - 1) {
      out[k] = 1.0001
      break
    }
    const want = tempo(k / (frames - 1))
    while (bin < BINS - 1 && cdf[bin]! < want) bin++
    out[k] = (bin + 1) / BINS
  }
  return out
}
